import {
  useGetDemoQuery,
  useGetPresentationsByDemoQuery,
} from '@carboncopyco/data';
import {
  BrowserIcon,
  CanvasGraphicsIcon,
  DurationOutlineIcon,
  TrendingOutlineIcon,
} from '@carboncopyco/icons';
import { useBoolean } from 'ahooks';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DiscardChangesModal, useSpeakerNotes } from '../../SpeakerNotes';
import { InfoPageSidebar, type TabButtons } from '../InfoPageSidebar';
import { SpeakerNotesIcon } from '../SpeakerNotesIcon';
import { useOverlayPageProps } from './OverlayPageContext';
import type { OverlayPageTab } from './types';

export const OverlayPageSidebar = () => {
  const [showDiscardModal, setShowDiscardModal] = useBoolean();
  const {
    goBack,
    demoId,
    organizationId,
    rootPath,
    noteValue,
    setNoteValue,
    pendingNavigationRoute,
    setPendingNavigationRoute,
    workspaceId,
  } = useOverlayPageProps();
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const { data: presentationsResponse, isLoading: isLoadingPresentations } =
    useGetPresentationsByDemoQuery({
      organizationId,
      workspaceId,
      demoId,
    });

  const tabButtons = useMemo<TabButtons<OverlayPageTab>>(() => {
    return {
      overview: {
        label: 'Overview',
        icon: <BrowserIcon size={18} />,
      },
      presentations: {
        label:
          isLoadingPresentations || !presentationsResponse?.metadata.totalItems
            ? 'Presentations'
            : `Presentations (${presentationsResponse?.metadata.totalItems})`,
        icon: <CanvasGraphicsIcon size={18} />,
      },
      activity: {
        label: 'Activity',
        icon: <TrendingOutlineIcon size={18} />,
      },
      history: {
        label: 'History',
        icon: <DurationOutlineIcon size={18} />,
      },
      'speaker-notes': {
        label: 'Speaker notes',
        icon: (
          <SpeakerNotesIcon
            organizationId={organizationId}
            entityType="demo"
            entityId={demoId}
          />
        ),
      },
    };
  }, [demoId, organizationId, presentationsResponse, isLoadingPresentations]);

  const navigate = useNavigate();
  const { note: savedNote } = useSpeakerNotes({
    entityType: 'demo',
    entityId: demoId,
  });
  const { tabId: currentTabId } = useParams();
  const hasUnsavedChanges = savedNote
    ? savedNote.content !== noteValue
    : !!noteValue;
  const handleChange = (tabId: string) => {
    const route = `${rootPath}/${tabId}`;
    if (hasUnsavedChanges) {
      setPendingNavigationRoute(route);
      setShowDiscardModal.setTrue();
      return;
    }
    navigate(route);
  };

  return (
    <>
      <InfoPageSidebar<OverlayPageTab>
        handleChange={
          currentTabId === 'speaker-notes' ? handleChange : undefined
        }
        tabButtons={tabButtons}
        screenshot={demo?.image}
        rootPath={rootPath}
        goBack={goBack}
        isOverlayDemo={demo?.recordingType === 'live_demo'}
      />
      {showDiscardModal && (
        <DiscardChangesModal
          onDiscard={() => {
            setNoteValue(savedNote?.content);
            setShowDiscardModal.setFalse();
            navigate(pendingNavigationRoute);
          }}
          onCancel={setShowDiscardModal.setFalse}
        />
      )}
    </>
  );
};
